import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AppShell from "../shell/AppShell.jsx";
import DetailDrawer from "../components/DetailDrawer.jsx";
import { useBackofficeIdentity } from "../hierarchy/BackofficeIdentityContext.jsx";
import { usePlcsList, PLCS_DEFAULT_PAGE_SIZE } from "./usePlcsList.js";
import { useSuperAgentsList } from "./useSuperAgentsList.js";
import { getPlc, createPlc, updatePlc } from "./workerLineApi.js";
import { PlcDetailView, PlcCreateView, PlcEditView, emptyPlcForm } from "./PlcViews.jsx";
import {
  WORKER_LINE_STATUS_OPTIONS,
  statusBadgeVariant,
  describeListError,
  isJobDemandSide,
  middleTierScreenLabel,
  middleTierScreenDescription,
  agentLineNoun,
  organizationSideLabel,
} from "./workerLineShared.js";
import "./workerLine.css";
import "../pages/states.css";
import Pagination from "../components/Pagination.jsx";

import PageSkeleton from "../components/Skeleton.jsx";
const DRAWER_TITLE_ID = "plc-drawer-title";
const SEARCH_DEBOUNCE_MS = 350;
// The Worker Line Admin API has no dedicated "all Super Agents" endpoint -
// this is the same real, paginated list every other screen uses, just
// asking for the backend's own maximum page size (WorkerLineAdminService.
// MaxPageSize = 100) so the Super Agent filter/parent dropdowns are as
// complete as the API allows in one request.
const SUPER_AGENT_OPTIONS_PAGE_SIZE = 100;

/**
 * /backoffice/worker-line/plcs - Prompt 132 Worker Line Admin: the middle tier
 * of a line. Entirely over the real Worker Line Admin API
 * (GET/POST/PATCH /api/backoffice/worker-line/plcs[/{id}]). A PLC always has
 * a real parent Super Agent - the Create PLC drawer's own dropdown is
 * sourced from GET .../worker-line/super-agents, never a fake/static list.
 * Reachable both from the sidebar and from a Super Agent detail's own
 * "next actions" link (?createForSuperAgentId=... auto-opens Create
 * prefilled with that parent).
 *
 * P161: the same screen now serves three URLs, distinguished only by `side`:
 *
 *   /plcs              side=null           both sides, the unfiltered list this
 *                                          route has always been. Not in the
 *                                          menu any more, but still where the
 *                                          Super Agent / Org Chart "สร้าง ...
 *                                          ใต้สายงานนี้" links land, because
 *                                          those already name their parent and
 *                                          must not be filtered to a side that
 *                                          parent is not on.
 *   /plcs/job-side     side="JobDemand"    บริษัทจัดหางาน
 *   /plcs/worker-side  side="WorkerSupply" บริษัทจัดหาแรงงาน
 *
 * The side is sent to the API as a real `organizationSide` filter rather than
 * being applied to the fetched page in the browser - filtering after the fact
 * would leave the row count, the pager and the "no results" state describing a
 * different set than the one on screen.
 */
export default function PlcsListPage({ side = null }) {
  const { data: identity } = useBackofficeIdentity();
  const primaryRole = identity?.primaryRole;
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [superAgentFilter, setSuperAgentFilter] = useState("");
  const [page, setPage] = useState(1);

  const [selectedId, setSelectedId] = useState(null);
  const [drawerView, setDrawerView] = useState(null); // null | "detail" | "create" | "edit"
  const [detailState, setDetailState] = useState({ status: "idle", data: null, error: null, forId: null });
  const [detailReloadToken, setDetailReloadToken] = useState(0);

  const [createForm, setCreateForm] = useState(emptyPlcForm());
  const [createValidationError, setCreateValidationError] = useState(null);
  const [createSubmit, setCreateSubmit] = useState({ status: "idle", error: null });

  const [editForm, setEditForm] = useState(null);
  const [editValidationError, setEditValidationError] = useState(null);
  const [editSubmit, setEditSubmit] = useState({ status: "idle", error: null });

  const superAgentOptionsFilters = useMemo(() => ({ page: 1, pageSize: SUPER_AGENT_OPTIONS_PAGE_SIZE }), []);
  const { data: superAgentOptionsData } = useSuperAgentsList(superAgentOptionsFilters);
  // A PLC inherits its parent Super Agent's side, so on a side-scoped screen the
  // parent picker only offers parents on that side. Offering the others would
  // let an operator create a company that vanishes from the screen they created
  // it on. The Super Agent list API has no side filter of its own, so this is a
  // narrowing of an already-complete single page of options, not a paged list
  // being silently cut short.
  const allSuperAgentOptions = superAgentOptionsData?.items ?? [];
  const superAgentOptions = useMemo(
    () =>
      side === null
        ? allSuperAgentOptions
        : allSuperAgentOptions.filter((option) => isJobDemandSide(option.organizationSide) === isJobDemandSide(side)),
    [allSuperAgentOptions, side]
  );

  const screenLabel = side === null ? "PLC / บมจ" : middleTierScreenLabel(side);
  const createButtonLabel = side === null ? "+ Create PLC" : `+ สร้าง${screenLabel}`;
  const searchPlaceholder = side === null ? "ค้นหารหัส PLC หรือชื่อ PLC..." : `ค้นหารหัสหรือชื่อ${screenLabel}...`;
  const listPanelTitle = side === null ? "รายการ PLC / บมจ" : `รายการ${screenLabel}`;
  const loadingText = side === null ? "กำลังโหลดรายการ PLC..." : `กำลังโหลดรายการ${screenLabel}...`;
  const emptyText = side === null ? "ยังไม่มี PLC ในระบบ" : `ยังไม่มี${screenLabel}ในระบบ`;
  const screenDescription =
    side === null ? "จัดการ PLC ทุกแห่งใต้ Super Agent ทุกสายงาน สร้างและแก้ไขข้อมูลได้จากหน้านี้" : middleTierScreenDescription(side);
  const crumbs = ["Backoffice", "SuperAdmin", "Worker Line", screenLabel];
  // The job side counts employers, the worker side counts workers - see
  // WorkerLinePlcListItemDto.employerCount. On the unfiltered list the rows can
  // be of either side, so it keeps the worker column it has always had and each
  // row carries its own side badge instead.
  const lineNoun = side === null ? "แรงงาน" : agentLineNoun(side);
  const lineCountOf = (item) => (isJobDemandSide(side ?? item.organizationSide) ? (item.employerCount ?? 0) : (item.workerCount ?? 0));

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, superAgentFilter, side]);

  const filters = useMemo(
    () => ({
      page,
      pageSize: PLCS_DEFAULT_PAGE_SIZE,
      search,
      status: statusFilter,
      superAgentOrganizationId: superAgentFilter,
      organizationSide: side ?? "",
    }),
    [page, search, statusFilter, superAgentFilter, side]
  );

  const { status: listStatus, data: listData, error: listError, refetch: refetchList } = usePlcsList(filters);

  useEffect(() => {
    if (!selectedId) {
      setDetailState({ status: "idle", data: null, error: null, forId: null });
      return undefined;
    }

    let cancelled = false;
    setDetailState((previous) => ({
      status: "loading",
      data: previous.forId === selectedId ? previous.data : null,
      error: null,
      forId: selectedId,
    }));

    getPlc(selectedId)
      .then((data) => {
        if (!cancelled) {
          setDetailState({ status: "ready", data, error: null, forId: selectedId });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setDetailState({ status: "error", data: null, error, forId: selectedId });
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- detailReloadToken intentionally re-triggers a fetch for the same selectedId
  }, [selectedId, detailReloadToken]);

  function openDetail(id) {
    setSelectedId(id);
    setDrawerView("detail");
    setEditForm(null);
    setEditValidationError(null);
    setEditSubmit({ status: "idle", error: null });
  }

  function openCreate(prefillSuperAgentId) {
    setCreateForm(emptyPlcForm(prefillSuperAgentId ? { superAgentOrganizationId: prefillSuperAgentId } : {}));
    setCreateValidationError(null);
    setCreateSubmit({ status: "idle", error: null });
    setDrawerView("create");
  }

  function openEdit() {
    const detail = detailState.data;
    if (!detail) {
      return;
    }
    setEditForm({
      displayName: detail.displayName ?? "",
      legalName: detail.legalName ?? "",
      ownerName: detail.ownerName ?? "",
      primaryPhoneNumber: detail.primaryPhoneNumber ?? "",
      lineId: detail.lineId ?? "",
      status: detail.status ?? "Active",
      note: detail.note ?? "",
    });
    setEditValidationError(null);
    setEditSubmit({ status: "idle", error: null });
    setDrawerView("edit");
  }

  function closeDrawer() {
    setDrawerView(null);
    setSelectedId(null);
    if (searchParams.has("createForSuperAgentId")) {
      const next = new URLSearchParams(searchParams);
      next.delete("createForSuperAgentId");
      setSearchParams(next, { replace: true });
    }
  }

  // A Super Agent detail's "next actions" links here with
  // ?createForSuperAgentId=<id> - open Create prefilled with that parent
  // instead of requiring the parent to be re-picked from the dropdown.
  useEffect(() => {
    const prefillId = searchParams.get("createForSuperAgentId");
    if (prefillId && drawerView === null) {
      openCreate(prefillId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount only
  }, []);

  function handleCreateSubmit() {
    if (!createForm.superAgentOrganizationId) {
      setCreateValidationError("กรุณาเลือก Super Agent ต้นสังกัด");
      return;
    }
    const trimmedName = createForm.displayName.trim();
    if (!trimmedName) {
      setCreateValidationError("กรุณาระบุ displayName");
      return;
    }
    setCreateValidationError(null);
    setCreateSubmit({ status: "submitting", error: null });
    createPlc({ ...createForm, displayName: trimmedName })
      .then(() => {
        setCreateSubmit({ status: "success", error: null });
        refetchList();
        closeDrawer();
      })
      .catch((error) => {
        setCreateSubmit({ status: "error", error });
      });
  }

  function handleEditSubmit() {
    if (!editForm) {
      return;
    }
    const trimmedName = editForm.displayName.trim();
    if (!trimmedName) {
      setEditValidationError("displayName ไม่สามารถเว้นว่างได้");
      return;
    }
    setEditValidationError(null);
    setEditSubmit({ status: "submitting", error: null });
    updatePlc(selectedId, { ...editForm, displayName: trimmedName })
      .then(() => {
        setEditSubmit({ status: "success", error: null });
        setDetailReloadToken((token) => token + 1);
        refetchList();
        setDrawerView("detail");
      })
      .catch((error) => {
        setEditSubmit({ status: "error", error });
      });
  }

  const items = listData?.items ?? [];
  const totalCount = listData?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PLCS_DEFAULT_PAGE_SIZE));
  const hasActiveFilters = Boolean(search || statusFilter || superAgentFilter);

  return (
    <AppShell crumbs={crumbs}>
      <div className="wx-wl-page">
        <header className="wx-wl-head">
          <div>
            <h1>{screenLabel}</h1>
            <p>{screenDescription}</p>
          </div>
          {primaryRole && (
            <span className="wx-wl-scope-pill">
              Scope <b>{primaryRole}</b>
            </span>
          )}
        </header>

        <div className="wx-wl-toolbar">
          <label className="wx-wl-search">
            <span className="wx-visually-hidden">Search PLC code or name</span>
            <input
              type="search"
              placeholder={searchPlaceholder}
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </label>

          <div className="wx-wl-toolbar-actions">
            <button type="button" className="wx-button wx-button--primary wx-wl-create-button" onClick={() => openCreate()}>
              {createButtonLabel}
            </button>
          </div>

          <div className="wx-wl-filters">
            <label className="wx-wl-filter">
              <span>Super Agent</span>
              <select value={superAgentFilter} onChange={(event) => setSuperAgentFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {superAgentOptions.map((option) => (
                  <option key={option.superAgentOrganizationId} value={option.superAgentOrganizationId}>
                    {option.displayName}
                  </option>
                ))}
              </select>
            </label>
            <label className="wx-wl-filter">
              <span>Status</span>
              <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {WORKER_LINE_STATUS_OPTIONS.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        <section className="wx-panel wx-wl-list-panel">
          <div className="wx-wl-list-head">
            <h2 className="wx-panel-title">{listPanelTitle}</h2>
            <span className="wx-wl-list-count">
              {totalCount} รายการ{listStatus === "loading" && listData !== null ? " · กำลังโหลด..." : ""}
            </span>
          </div>

          {listStatus === "error" && listData !== null && (
            <p className="wx-wl-inline-error" role="alert">
              {describeListError(listError)}
            </p>
          )}

          {listStatus === "error" && listData === null ? (
            <div className="wx-state-card wx-state-card--danger" role="alert">
              <p className="wx-state-body">{describeListError(listError)}</p>
            </div>
          ) : listStatus === "loading" && listData === null ? (
            <PageSkeleton variant="table" label="{loadingText}" />
          ) : items.length === 0 ? (
            <p className="wx-empty-list">{hasActiveFilters ? "ไม่พบผลลัพธ์ที่ตรงกับตัวกรองหรือคำค้นหา" : emptyText}</p>
          ) : (
            <>
              <div className="wx-wl-table-wrap">
                <table className="wx-wl-table">
                  <thead>
                    <tr>
                      <th>{screenLabel}</th>
                      <th>Super Agent ต้นสังกัด</th>
                      <th>ผู้ดูแล</th>
                      <th>phone</th>
                      <th>Status</th>
                      <th>Agent</th>
                      <th>{lineNoun}</th>
                      <th aria-hidden="true">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.plcOrganizationId} className="wx-wl-row" onClick={() => openDetail(item.plcOrganizationId)}>
                        <td>
                          <strong className="wx-wl-cell-strong">{item.displayName}</strong>
                          <span className="wx-wl-code">{item.plcCode}</span>
                          {side === null && <span className="wx-wl-code">{organizationSideLabel(item.organizationSide)}</span>}
                        </td>
                        <td>{item.superAgentName || "-"}</td>
                        <td>{item.ownerName || "-"}</td>
                        <td>{item.primaryPhoneNumber || "-"}</td>
                        <td>
                          <span className={`wx-badge wx-badge--${statusBadgeVariant(item.status)}`}>{item.status}</span>
                        </td>
                        <td>{item.agentCount}</td>
                        <td>{lineCountOf(item)}</td>
                        <td>
                          <button
                            type="button"
                            className="wx-wl-row-action"
                            onClick={(event) => {
                              event.stopPropagation();
                              openDetail(item.plcOrganizationId);
                            }}
                          >
                            เปิดรายละเอียด
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="wx-wl-cards">
                {items.map((item) => (
                  <div className="wx-wl-card" key={item.plcOrganizationId} onClick={() => openDetail(item.plcOrganizationId)}>
                    <div className="wx-wl-card-top">
                      <div>
                        <strong>{item.displayName}</strong>
                        <span className="wx-wl-card-sub">
                          {item.plcCode} · {item.superAgentName || "-"}
                        </span>
                      </div>
                      <span className={`wx-badge wx-badge--${statusBadgeVariant(item.status)}`}>{item.status}</span>
                    </div>
                    <div className="wx-wl-card-grid">
                      <div>
                        <label>Agent</label>
                        <strong>{item.agentCount}</strong>
                      </div>
                      <div>
                        <label>{lineNoun}</label>
                        <strong>{lineCountOf(item)}</strong>
                      </div>
                      <div>
                        <label>phone</label>
                        <strong>{item.primaryPhoneNumber || "-"}</strong>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="wx-wl-row-action wx-wl-row-action--full"
                      onClick={(event) => {
                        event.stopPropagation();
                        openDetail(item.plcOrganizationId);
                      }}
                    >
                      เปิดรายละเอียด
                    </button>
                  </div>
                ))}
              </div>

              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </>
          )}
        </section>
      </div>

      <DetailDrawer
        open={drawerView !== null}
        onClose={closeDrawer}
        labelledBy={DRAWER_TITLE_ID}
        selectableSelector="tr.wx-wl-row, .wx-wl-card, .wx-wl-create-button"
      >
        {drawerView === "detail" && (
          <PlcDetailView
            titleId={DRAWER_TITLE_ID}
            state={detailState}
            onEdit={openEdit}
            onCreateAgent={(superAgentId, plcId) => `/backoffice/worker-line/agents?createForSuperAgentId=${superAgentId}&createForPlcId=${plcId}`}
          />
        )}
        {drawerView === "create" && (
          <PlcCreateView
            titleId={DRAWER_TITLE_ID}
            form={createForm}
            onChange={(patch) => setCreateForm((previous) => ({ ...previous, ...patch }))}
            superAgentOptions={superAgentOptions}
            validationError={createValidationError}
            submitState={createSubmit}
            onSubmit={handleCreateSubmit}
          />
        )}
        {drawerView === "edit" && editForm && (
          <PlcEditView
            titleId={DRAWER_TITLE_ID}
            code={detailState.data?.plcCode}
            form={editForm}
            onChange={(patch) => setEditForm((previous) => ({ ...previous, ...patch }))}
            validationError={editValidationError}
            submitState={editSubmit}
            onSubmit={handleEditSubmit}
            onBack={() => setDrawerView("detail")}
          />
        )}
      </DetailDrawer>
    </AppShell>
  );
}
